import React from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, Area, AreaChart, Cell,
} from "recharts";

const AXIS = { stroke: "#3f4b52", fontSize: 11, fontFamily: "JetBrains Mono" };
const GRID = "#1B2226";

const tooltipStyle = {
  backgroundColor: "#14181B",
  border: "1px solid #242B30",
  borderRadius: "10px",
  fontFamily: "JetBrains Mono",
  fontSize: "12px",
  color: "#F4F4F5",
};

function Panel({ title, subtitle, children, testId }) {
  return (
    <div data-testid={testId} className="bg-[#14181B] border border-[#242B30] rounded-xl p-4 sm:p-5">
      <div className="mb-4">
        <h3 className="font-display text-base font-semibold text-white">{title}</h3>
        {subtitle && <p className="text-[11px] text-zinc-500 font-mono mt-0.5">{subtitle}</p>}
      </div>
      <div className="h-[230px] sm:h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export function SpendClicksChart({ data }) {
  const fmt = (d) => d.slice(5);
  return (
    <Panel title="Spend vs Outbound Clicks" subtitle="Daily delivery trend" testId="chart-spend-clicks">
      <AreaChart data={data} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
        <defs>
          <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A3E635" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#A3E635" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={fmt} {...AXIS} />
        <YAxis yAxisId="l" {...AXIS} />
        <YAxis yAxisId="r" orientation="right" {...AXIS} />
        <Tooltip contentStyle={tooltipStyle} />
        <Area yAxisId="l" type="monotone" dataKey="spend" stroke="#A3E635" strokeWidth={2} fill="url(#spendGrad)" name="Spend ($)" />
        <Line yAxisId="r" type="monotone" dataKey="outbound_clicks" stroke="#06B6D4" strokeWidth={2} dot={false} name="Clicks" />
      </AreaChart>
    </Panel>
  );
}

export function FrequencyCpmChart({ data }) {
  const fmt = (d) => d.slice(5);
  return (
    <Panel title="Frequency vs CPM" subtitle="Fatigue & efficiency signal" testId="chart-freq-cpm">
      <LineChart data={data} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={fmt} {...AXIS} />
        <YAxis yAxisId="l" {...AXIS} />
        <YAxis yAxisId="r" orientation="right" {...AXIS} />
        <Tooltip contentStyle={tooltipStyle} />
        <Line yAxisId="l" type="monotone" dataKey="frequency" stroke="#F59E0B" strokeWidth={2} dot={false} name="Frequency" />
        <Line yAxisId="r" type="monotone" dataKey="cpm" stroke="#F43F5E" strokeWidth={2} dot={false} name="CPM ($)" />
      </LineChart>
    </Panel>
  );
}

export function CountryReachChart({ data }) {
  const sorted = [...data].sort((a, b) => b.reach - a.reach).slice(0, 8);
  const colors = ["#A3E635", "#84cc16", "#65a30d", "#10B981", "#06B6D4", "#0ea5e9", "#F59E0B", "#F43F5E"];
  return (
    <Panel title="Reach by Country" subtitle="Top markets" testId="chart-country-reach">
      <BarChart data={sorted} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
        <XAxis dataKey="country_name" {...AXIS} interval={0} angle={-20} textAnchor="end" height={50} />
        <YAxis {...AXIS} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(163,230,53,0.05)" }} />
        <Bar dataKey="reach" radius={[4, 4, 0, 0]} name="Reach">
          {sorted.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
        </Bar>
      </BarChart>
    </Panel>
  );
}

export function CtrByCampaignChart({ data }) {
  const sorted = [...data].sort((a, b) => b.ctr - a.ctr);
  return (
    <Panel title="CTR by Campaign" subtitle="Click-through rate %" testId="chart-ctr-campaign">
      <BarChart data={sorted} layout="vertical" margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
        <XAxis type="number" {...AXIS} />
        <YAxis type="category" dataKey="campaign_name" {...AXIS} width={140}
          tickFormatter={(v) => (v.length > 18 ? v.slice(0, 18) + "…" : v)} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(163,230,53,0.05)" }} />
        <Bar dataKey="ctr" fill="#A3E635" radius={[0, 4, 4, 0]} name="CTR %" />
      </BarChart>
    </Panel>
  );
}
